import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Clock, Wrench, Flag } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const tabIds = ["ai", "digital", "business", "leadership"];
const tabTestIds = {
  ai: "program-tab-ai-applications",
  digital: "program-tab-digital-skills",
  business: "program-tab-business-innovation",
  leadership: "program-tab-leadership",
};

export const ProgramsCatalog = () => {
  const { t } = useLanguage();
  const [active, setActive] = useState("ai");
  const program = t.programs.items.find((p) => p.id === active);

  const scrollToEnroll = () => document.getElementById("enroll")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="programs" className="py-20 sm:py-28 bg-[#163832] text-[#FDFCF7]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.p initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.5 }}
          className="text-xs font-mono uppercase tracking-[0.22em] text-amber-400 font-semibold">
          {t.programs.overline}
        </motion.p>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mt-4 mb-10">
          <motion.h2 initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6, delay: 0.1 }}
            className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight max-w-xl" data-testid="programs-heading">
            {t.programs.heading}
          </motion.h2>
          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }}
            className="text-sm font-mono text-[#FDFCF7]/60">{t.programs.sub}</motion.p>
        </div>

        <div className="flex flex-wrap gap-2 mb-8" role="tablist">
          {tabIds.map((id) => {
            const item = t.programs.items.find((p) => p.id === id);
            return (
              <button key={id} role="tab" aria-selected={active === id} data-testid={tabTestIds[id]}
                onClick={() => setActive(id)}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold border transition-colors duration-200 ${
                  active === id
                    ? "bg-[#C85A17] border-[#C85A17] text-[#FDFCF7]"
                    : "border-[#FDFCF7]/20 text-[#FDFCF7]/70 hover:border-[#C85A17] hover:text-[#FDFCF7]"
                }`}>
                {item.tab}
              </button>
            );
          })}
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={active + t.programs.heading}
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="program-card">
            <div className="lg:col-span-7 rounded-2xl border border-[#FDFCF7]/10 bg-[#FDFCF7]/[0.04] p-8 sm:p-10">
              <div className="flex flex-wrap items-center gap-3 mb-5">
                <span className="text-xs font-mono uppercase tracking-widest bg-[#C85A17] text-[#FDFCF7] rounded-full px-3 py-1">{program.badge}</span>
                <span className="text-xs font-mono uppercase tracking-widest text-[#FDFCF7]/60 flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" />{program.duration}
                </span>
              </div>
              <h3 className="font-display text-xl sm:text-2xl font-semibold tracking-tight" data-testid="program-card-title">{program.title}</h3>
              <p className="mt-4 text-sm sm:text-base leading-relaxed text-[#FDFCF7]/70 max-w-lg">{program.desc}</p>
              <button data-testid="program-card-enroll-button" onClick={scrollToEnroll}
                className="group mt-8 inline-flex items-center gap-2 bg-[#FDFCF7] text-[#163832] font-semibold rounded-full px-6 py-3 hover:bg-[#C85A17] hover:text-[#FDFCF7] transition-colors duration-200">
                {t.programs.cta}
                <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </div>
            <div className="lg:col-span-5 grid grid-rows-2 gap-6">
              <div className="rounded-2xl border border-[#FDFCF7]/10 bg-[#FDFCF7]/[0.04] p-7">
                <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-amber-400 mb-4">
                  <Wrench className="w-3.5 h-3.5" />{t.programs.stackLabel}
                </div>
                <div className="flex flex-wrap gap-2">
                  {program.stack.map((s) => (
                    <span key={s} className="text-sm border border-[#FDFCF7]/20 rounded-full px-3.5 py-1.5 text-[#FDFCF7]/85">{s}</span>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl border border-[#C85A17]/40 bg-[#C85A17]/10 p-7">
                <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-amber-400 mb-3">
                  <Flag className="w-3.5 h-3.5" />{t.programs.capstoneLabel}
                </div>
                <p className="text-sm sm:text-base leading-relaxed text-[#FDFCF7]/85">{program.capstone}</p>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};
